import { AxiosInstance } from "../../../shared/utils/axios";
import { type NutrientTrial } from "../utils/nutrientApi";
import { type NutrientGroup } from "../utils/nutrientPlots";

export interface NutrientAutoAssignmentResponse {
  assignments: Record<string, number>;
  levels: number;
}

export const requestNutrientAutoAssignment = async (
  trialId: string,
  groups: NutrientGroup[],
  isStripMode = false
): Promise<NutrientAutoAssignmentResponse> => {
  if (!trialId.trim()) {
    throw new Error("A saved trial is required for automatic assignment.");
  }

  const { data } = await AxiosInstance.get<NutrientTrial>(
    `/trial/${encodeURIComponent(trialId)}/auto-assign-plot-properties`,
    { params: { trial_type: "nutrient" } }
  );
  if (!Array.isArray(data?.trial_plots)) {
    throw new TypeError(
      "The automatic assignment API returned invalid plot data."
    );
  }

  const trialPlots = data.trial_plots;
  const stripRates = isStripMode
    ? trialPlots.flatMap(
        (entry) => entry.properties?.fertilisers?.rates_and_dosages ?? []
      )
    : [];
  let stripIndex = 0;
  const assignments = Object.fromEntries(
    groups.flatMap((group, groupIndex) => {
      const entry = trialPlots[groupIndex];
      const rates = entry?.properties?.fertilisers?.rates_and_dosages ?? [];
      return group.plots.map((plot, cellIndex) => {
        const rate = isStripMode
          ? stripRates[stripIndex++]?.rate
          : rates[cellIndex]?.rate;
        if (typeof rate !== "number" || !Number.isFinite(rate)) {
          throw new TypeError(
            "The automatic assignment API returned incomplete nutrient rates."
          );
        }
        return [plot.id, rate];
      });
    })
  );

  return {
    assignments,
    levels: new Set(Object.values(assignments)).size,
  };
};
